/** @jsx jsx */
import { jsx } from '@emotion/core';
import { FC, ReactNode, useState, useEffect } from 'react';
import { Flex, Text, Box, Link, Heading, BoxProps } from 'rebass';
import MainContainer from '../../ui-components/container/container';
import { Cards } from './curriculum/cards';

import Filter from '../../ui-components/filters/filter';
import { Curriculum } from '../../templates/curriculum-hub';
import ordinal from 'ordinal';
import { gridColumn, gridTemplateColumns } from 'styled-system';

interface FilteredCardsSectionProps {
  className?: string;
  id?: string;
  children?: ReactNode;
  showDefaultHeading?: boolean;
  // selectOptions?: Array<SelectOptions[]>;
  data?: any; // Array<{}> | Curriculum[]; // data comes in raw, this component filters the data and correctly passes it into the cards
  keysToFilterOn?: string[];

  css?: any;
}

export interface SelectOptions {
  name?: string;
  value: string;
  label: string;
  disabled?: boolean;
}

export interface NodeCurriculum {
  node: Curriculum;
}

const FilteredCardsSection: FC<FilteredCardsSectionProps> = ({
  className = '',
  id = '',
  children,
  // selectOptions,
  data,
  keysToFilterOn,
  showDefaultHeading = true,
  ...rest
}) => {
  const sortNumbersAndStrings = array =>
    array.sort((a, b) => {
      if (isNaN(a) && isNaN(b)) {
        return a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      } else {
        if (a > b) {
          return 1;
        }
        if (a < b) {
          return -1;
        }
        return 0;
      }
    });
  const [filters, setFilters] = useState([]);
  const setFilterByIndex = (options: SelectOptions[], filtersIndex: number) =>
    setFilters(Object.assign([], filters, { [filtersIndex]: options }));
  const generateSelects = (data: NodeCurriculum[], filteringOn: string[]) =>
    filteringOn.map(filterItem => {

      if (filterItem === 'length') {
        return {
          name: filterItem,
          options: [
            ...new Set(data.map(({ node: datum }) => datum[filterItem]).flat())
          ]
            .sort(
              (a, b) =>
                a &&
                b &&
                a.toString().localeCompare(b.toString(), 'en-US', {
                  numeric: true,
                  ignorePunctuation: true
                })
            )
            .map(val => {
              val = `${val}+`;
              return val ? { name: filterItem, value: val, label: val } : {};
            })
        };
      } else if (filterItem === 'experienceYear') {
        return {
          name: filterItem,
          options: [
            ...new Set(data.map(({ node: datum }) => datum[filterItem]).flat())
          ]
            .sort(
              (a, b) =>
                a &&
                b &&
                a.toString().localeCompare(b.toString(), 'en-US', {
                  numeric: true,
                  ignorePunctuation: true
                })
            )
            .map(val => {
              // @ts-ignore
              const label = `${ordinal(val)} year`;
              return val && val > 0
                ? { name: filterItem, value: val, label }
                : {};
            })
        };
      } else if (filterItem === 'experienceQuarter') {
        return {
          name: filterItem,
          options: [
            ...new Set(data.map(({ node: datum }) => datum[filterItem]).flat())
          ]
            .sort(
              (a, b) =>
                a &&
                b &&
                a.toString().localeCompare(b.toString(), 'en-US', {
                  numeric: true,
                  ignorePunctuation: true
                })
            )
            .map(val => {
              // @ts-ignore
              const label = `${ordinal(val)} quarter`;
              return val && val > 0
                ? { name: filterItem, value: val, label }
                : {};
            })
        };
      }
      return {
        name: filterItem,
        options: [
          ...new Set(data.map(({ node: datum }) => datum[filterItem]).flat())
        ]
          .sort(
            (a, b) =>
              a &&
              b &&
              a.toString().localeCompare(b.toString(), 'en-US', {
                numeric: true,
                ignorePunctuation: true
              })
          )
          .map(val => {
            return val ? { name: filterItem, value: val, label: val } : {};
          })
      };
    });
  const [selects, setSelects] = useState(generateSelects(data, keysToFilterOn));
  const generateCards = (
    data: (object | NodeCurriculum)[],
    filters: SelectOptions[][]
  ) => {
    let filteredData = data;

    filteredData =
      filters && filters.flat().length > 0
        ? data.filter(({ node: datum }) => {
            const mappedEm = filters.map(oneSelectFilters => {
              return oneSelectFilters && oneSelectFilters.length > 0
                ? oneSelectFilters.some(({ name, value }) => {
                    const lookup = datum[name];
                    const lookupIsArray = Array.isArray(lookup);
                    const present = lookupIsArray
                      ? lookup.includes(value)
                      : lookup === value;

                    return present;
                  })
                : true;
            });
            // this map result is an array of true falses for each dropdown. If you want "or", use includes instead of "every"
            return Array.isArray(mappedEm)
              ? mappedEm.every(val => {
                  return val === undefined ? true : val;
                })
              : mappedEm;
          })
        : filteredData;

    return filteredData;
  };

  const [cards, setCards] = useState(generateCards(data, filters));

  useEffect(() => {
    setCards(generateCards(data, filters));
  }, [filters]);

  return (
    <Box backgroundColor="background" color="text" py={2} px={[0, 4]} {...rest}>
      <Box
        sx= {{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr 2fr', '1fr 3fr'],
          gridTemplateRows: ['1fr', '1fr 3fr'],
          gridColumnGap: '30px',
          pt: [4, 4],
          pl: [3, 3],
          pb: [4, 4],
          pr: [3, 3],
          maxWidth: '1200px',
          margin: '0 auto',
        }}
      >
        <Box
          mt={[1, 2]}
          sx= {{
            position: ['relative', 'sticky'],
            alignSelf: 'start',
            gridColumn: '1 / 2',
            gridRow: '1',
            top: '0',
            left: '0',
          }}
        >
          {showDefaultHeading && (
            <Heading as="h2" fontSize={[3, 5]}>
              Filter by:
            </Heading>
          )}
          {selects &&
            selects.map((select, i) => (
              <Box my={'.5rem'} mr={'.5rem'} key={select.name}>
                <Filter
                  label={`${select.name}`}
                  indexNumber={i}
                  options={select.options}
                  selected={filters[i]}
                  updateFunction={setFilterByIndex}
                />
              </Box>
            ))}
        </Box>
        <Box sx={{ gridColumn: ['1', '2 / 4'], gridRow: ['2', '1 / 3'] }}>
          <Cards cards={cards} mt={4} cardsPerRow={3} />
        </Box>
      </Box>
    </Box>
  );
};

export default FilteredCardsSection;
